import api from './api';

/**
 * The four item types with a reachable detail surface. Pro and template share
 * one route (`/pro/:pronumber` parses a `T`-prefixed template pronumber) but
 * stay distinct so the panel can label and icon them differently.
 */
export type FollowUpItemType = 'pro' | 'template' | 'quote' | 'driver';

export interface FollowUpItem {
  id: number;
  item_type: FollowUpItemType;
  /** pronumber, `T`-prefixed template pro, `quoteNumber-revision`, or driver unit. */
  item_id: string;
  /** Snapshot taken when bookmarked — NOT kept in sync with the source record. */
  label: string;
  note: string | null;
  /** Entry background as `#RRGGBB`, or null for the panel default. Free hex, so
   *  the panel derives a readable label colour from it (see followUpColors). */
  color: string | null;
  /** Manual drag position. null means never hand-placed, and the server sorts
   *  those FIRST -- a new bookmark returns to the top of a hand-ordered list. */
  sort_order: number | null;
  created_at: string;
  updated_at: string;
}

/** Fields the user can edit. Omit one to leave it untouched; null clears it. */
export interface FollowUpPatch {
  note?: string | null;
  color?: string | null;
}

const BASE = '/follow-ups';

/**
 * Thin transport for the Follow Up panel. All state, ordering, and optimistic
 * behaviour lives in FollowUpContext — this file only speaks HTTP.
 */
const followUpsService = {
  /** The caller's follow-ups, newest first (the server does the ordering). */
  async list(): Promise<FollowUpItem[]> {
    const res = await api.get<{ items: FollowUpItem[] }>(BASE);
    return res.data.items ?? [];
  },

  /** Bookmark an item. Idempotent server-side on (user, type, id). */
  async add(item_type: FollowUpItemType, item_id: string, label: string): Promise<FollowUpItem> {
    const res = await api.post<FollowUpItem>(BASE, { item_type, item_id, label });
    return res.data;
  },

  /**
   * Patch the note and/or the colour. Only the keys present are written, so a
   * colour change cannot blank a note the user just typed. Whitespace-only
   * clears either field.
   */
  async update(id: number, patch: FollowUpPatch): Promise<FollowUpItem> {
    const res = await api.patch<FollowUpItem>(`${BASE}/${id}`, patch);
    return res.data;
  },

  /**
   * Commit a drag: ids top to bottom. Returns the server's full list, which may
   * include rows this client never had (e.g. bookmarked in another tab), so the
   * caller should adopt the response rather than keep its local guess.
   */
  async reorder(ids: number[]): Promise<FollowUpItem[]> {
    const res = await api.patch<{ items: FollowUpItem[] }>(`${BASE}/reorder`, { ids });
    return res.data.items ?? [];
  },

  async remove(id: number): Promise<void> {
    await api.delete(`${BASE}/${id}`);
  },
};

export default followUpsService;
